import { toTotalMonths } from '@thesavingsgroup/date-utils/toTotalMonths';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import every from 'lodash/every';
import toNumber from 'lodash/toNumber';

import { DEFAULT_PREVIOUS_INCOME_MONTHS_AT } from '../constants/income';
import { UserIncomesDto } from '../dto/UserIncomesDto';
import { IncomeFrequencyEnum } from '../enums/IncomeFrequencyEnum';
import { ApplicantState } from '../types/ApplicantState';
import { incomeFrequencyToAPI } from './incomeFrequencyToAPI';

/**
 * Converts an amount held in state to the value the API expects.
 *
 * Returns null rather than 0 when the amount is absent, so the API can tell
 * "not provided" apart from a real zero. `toNumber(null)` is 0, and a 0 amount
 * overwrites a stored income amount on the server (CP-6812).
 */
const toAmount = (amount: string | null): number | null =>
  isNonEmptyString(amount) ? toNumber(amount) : null;

export const toUserIncomesDto = (applicant: ApplicantState): UserIncomesDto => {
  const userIncomesDto: UserIncomesDto = {
    additional: null,
    current: {
      amount: toAmount(applicant.currentIncomeGrossAnnual),
      employerName: applicant.currentIncomeEmployerName,
      employmentType: applicant.currentIncomeEmploymentType,
      incomeFrequency: incomeFrequencyToAPI(IncomeFrequencyEnum.ANNUALLY),
      jobTitle: applicant.currentIncomeJobTitle,
      monthsAt: toNumber(applicant.currentIncomeMonthsAt),
    },
    previous: null,
  };

  const shouldIncludeAdditional = every(
    [
      applicant.additionalIncomeType,
      applicant.additionalIncomeAmount,
      applicant.additionalIncomeFrequency,
    ],
    isNonEmptyString,
  );

  if (shouldIncludeAdditional) {
    userIncomesDto.additional = {
      additionalIncomeType: applicant.additionalIncomeType,
      amount: toAmount(applicant.additionalIncomeAmount),
      employerName: applicant.additionalIncomeEmployerName,
      employmentType: EmploymentType.OTHER,
      incomeFrequency: incomeFrequencyToAPI(
        applicant.additionalIncomeFrequency,
      ),
      jobTitle: applicant.additionalIncomeJobTitle,
      monthsAt: toTotalMonths({
        months: toNumber(applicant.additionalIncomeDurationMonths),
        years: toNumber(applicant.additionalIncomeDurationYears),
      }),
    };
  }

  const shouldIncludePrevious = every(
    [
      applicant.previousIncomeGrossAnnual,
      applicant.previousIncomeEmploymentType,
    ],
    isNonEmptyString,
  );

  if (shouldIncludePrevious) {
    userIncomesDto.previous = {
      amount: toAmount(applicant.previousIncomeGrossAnnual),
      employerName: applicant.previousIncomeEmployerName,
      employmentType: applicant.previousIncomeEmploymentType,
      incomeFrequency: incomeFrequencyToAPI(IncomeFrequencyEnum.ANNUALLY),
      jobTitle: applicant.previousIncomeJobTitle,
      monthsAt: DEFAULT_PREVIOUS_INCOME_MONTHS_AT,
    };
  }

  return userIncomesDto;
};
